'use client';

import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ScheduledDayColumn } from './scheduled-day-column.component';
import { ScheduledDaysHeader } from './scheduled-days-header.component';

import { Button } from '@/components/ui/button/button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { SlotsSkeleton } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_primitives/slots-skeleton.component';
import { useScheduleSlots } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_hooks/use-schedule-slots.hook';

const CARD_SLOTS_PER_COLUMN = 4;

interface ScheduledSlotsGridProps {
  scheduleId: string | undefined;
  limitDays: number;
  onSeeMore: () => void;
}

export function ScheduledSlotsGrid({ scheduleId, limitDays, onSeeMore }: ScheduledSlotsGridProps) {
  const { isLoading, days, hasMore, offset, goNext, goPrev } = useScheduleSlots({
    scheduleId,
    limitDays,
  });

  const maxSlotsInAnyDay = days.reduce((max, day) => Math.max(max, day.slots.length), 0);
  const canSeeMore = maxSlotsInAnyDay > CARD_SLOTS_PER_COLUMN;

  if (isLoading) {
    return <SlotsSkeleton variant="modal" columns={limitDays} />;
  }

  if (days.length === 0) {
    return (
      <Typography variant={TypographyVariant.BODY_TEXT} className="text-center text-gray-500 py-6">
        Nenhum horário disponível.
      </Typography>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex flex-col gap-2 w-full">
        <ScheduledDaysHeader
          days={days}
          prevDisabled={offset === 0}
          nextDisabled={!hasMore}
          onPrev={goPrev}
          onNext={goNext}
        />
        <div className="flex items-start justify-center w-full">
          <div className="w-8 shrink-0" />
          <div className="flex-1 flex gap-4 items-start min-w-0">
            {days.map((day) => (
              <ScheduledDayColumn
                key={day.date}
                slots={day.slots}
                visibleSlotsLimit={CARD_SLOTS_PER_COLUMN}
              />
            ))}
          </div>
          <div className="w-8 shrink-0" />
        </div>
      </div>
      {canSeeMore && (
        <Button
          variant="text"
          color="primary"
          startIcon={faPlus}
          startIconSize="xs"
          startIconClassName="text-xs text-green-500"
          className="self-center gap-2 py-1"
          data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_SCHEDULED_OPEN_MODAL}
          onClick={onSeeMore}
        >
          Ver outros horários
        </Button>
      )}
    </div>
  );
}
